import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  DashboardActiveProjectDetails,
  DashboardCustomerProgressSummary,
  DashboardCustomerTemplateSummary,
  DashboardProjectFormByShortCode,
  DashboardProjectManHoursAnalyticsResult,
  DashboardProjectCommissioningProgress,
  DashboardProjectSkidStatusSummary
} from '../../models/dashboard.model';
import { DashboardService } from '../../services/dashboard.service';
import { SSOService } from '../../services/sso.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { ChartModule } from 'primeng/chart';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, FormsModule, MultiSelectModule, ChartModule, ToggleSwitch, DialogModule],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.css'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = true;
  errorMessage = '';
  detailsExpanded = true;
  projectDetails: DashboardActiveProjectDetails | null = null;
  projectId: number | null = null;
  integrationSummary: DashboardProjectSkidStatusSummary | null = null;
  customerSkidSummary: DashboardCustomerProgressSummary | null = null;
  commissioningSummary: DashboardProjectCommissioningProgress | null = null;
  expandedCustomerTemplateId: number | null = null;
  analyticsLoading = true;
  analyticsErrorMessage = '';
  projectManHoursAnalytics: DashboardProjectManHoursAnalyticsResult | null = null;
  selectedManHoursUserIds: number[] = [];
  manHoursYAxis: number[] = [0];
  manHoursChartData: any = { labels: [], datasets: [] };
  manHoursChartOptions: any = {};
  categoryBreakdownChartData: any = { labels: [], datasets: [] };
  categoryBreakdownChartOptions: any = {};
  skidBreakdownChartData: any = { labels: [], datasets: [] };
  skidBreakdownChartOptions: any = {};
  manHoursDateRange = { startDate: '', endDate: '' };
  dateRangeDraft = { startDate: '', endDate: '' };
  showDateRangePopover = false;
  dateRangeError = '';
  showSkidBreakdownFullscreen = false;
  wmrForms: DashboardProjectFormByShortCode[] = [];
  ncrForms: DashboardProjectFormByShortCode[] = [];
  wmrLoading = false;
  ncrLoading = false;
  private readonly manHoursPalette = ['#3d5b83', '#ef3b67', '#63ae6f', '#9aa2b3', '#7a67c7', '#e0a104', '#5d8f95', '#68748f'];
  // Category colors are shared by the category and equipment charts. Keep the
  // first colors deliberately distinct; projects can introduce new categories.
  private readonly manHoursCategoryPalette = [
    '#0072B2', '#E69F00', '#009E73', '#CC79A7', '#D55E00', '#56B4E9',
    '#332288', '#44AA99', '#117733', '#999933', '#CC6677', '#882255',
    '#AA4499', '#661100', '#6699CC', '#DDCC77', '#88CCEE', '#6A3D9A'
  ];
  private readonly manHoursCategoryColors = new Map<string, string>();

  constructor(
    private route: ActivatedRoute,
    private dashboardService: DashboardService,
    private ssoService: SSOService,
    private permissionHelper: PermissionHelperService
  ) {
    this.manHoursDateRange = this.getCurrentMonthDateRange();
    this.manHoursChartOptions = this.buildProjectManHoursChartOptions();
    this.categoryBreakdownChartOptions = this.buildCategoryBreakdownChartOptions();
    this.skidBreakdownChartOptions = this.buildSkidBreakdownChartOptions();
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map(params => Number(params.get('projectId') ?? 0)),
        switchMap(projectId => {
          if (!Number.isFinite(projectId) || projectId <= 0) {
            this.loading = false;
            this.errorMessage = 'Invalid project id.';
            return of(null);
          }

          this.projectId = projectId;
          this.loading = true;
          this.errorMessage = '';
          this.projectDetails = null;
          this.integrationSummary = null;
          this.commissioningSummary = null;

          return this.resolveUserId().pipe(
            switchMap(userId => {
              if (!userId || userId <= 0) {
                this.errorMessage = 'Unable to identify current user.';
                return of(null);
              }

              return forkJoin({
                details: this.dashboardService.getMyActiveProjectDetails(projectId).pipe(catchError(() => of(null))),
                integration: this.dashboardService.getProjectSkidStatusSummaryByUser(userId, projectId).pipe(catchError(() => of([]))),
                commissioning: this.dashboardService.getMyCommissioningProgress(projectId).pipe(catchError(() => of([]))),
                customerSkid: this.dashboardService.getCustomerSkidStatusSummary(projectId).pipe(catchError(() => of(null)))
              });
            }),
            catchError(() => {
              this.errorMessage = 'Failed to load project dashboard data.';
              return of(null);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        if (result) {
          this.projectDetails = result.details;
          this.integrationSummary = (result.integration ?? [])[0] ?? null;
          this.commissioningSummary = (result.commissioning ?? [])[0] ?? null;
          this.customerSkidSummary = result.customerSkid ?? null;
          if (!this.projectDetails && !this.errorMessage) {
            this.errorMessage = 'Failed to load project dashboard data.';
          }
          if (this.projectId) {
            if (this.canView(PERMISSION_KEYS.CUMULATIVE_HOURS_PER_USER) || this.canView(PERMISSION_KEYS.HOURS_ALLOCATION_BY_CATEGORY) || this.canView(PERMISSION_KEYS.HOURS_ALLOCATION_BY_SKID)) {
              this.loadProjectManHoursAnalytics(this.projectId);
            }
            if (this.canView(PERMISSION_KEYS.WMR_DSR_WILDLIFE)) {
              this.loadProjectFormsByShortCode(this.projectId, 'WMR');
            }
            if (this.canView(PERMISSION_KEYS.NCR)) {
              this.loadProjectFormsByShortCode(this.projectId, 'NCR');
            }
          }
        }
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  canView(permissionKey: string): boolean {
    return this.permissionHelper.has(PAGE_KEYS.DASHBOARD, permissionKey);
  }

  toggleDetailsPanel(): void {
    this.detailsExpanded = !this.detailsExpanded;
  }

  getSafeValue(value?: string | null): string {
    const normalized = (value ?? '').trim();
    return normalized ? normalized : '-';
  }

  getPercent(value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${Math.round(safeValue)}%`;
  }

  getProjectNumbers(detail: DashboardActiveProjectDetails): string {
    const eq = this.getSafeValue(detail.projectNumber);
    const cx = this.getSafeValue(detail.commProject);
    const wr = this.getSafeValue(detail.warrantyProject);
    return `EQ: ${eq} | CX: ${cx} | WR: ${wr}`;
  }

  getCollapsedSiteManager(detail: DashboardActiveProjectDetails): string {
    const managers = detail.siteManagers?.filter(x => (x || '').trim().length > 0) ?? [];
    if (managers.length > 0) {
      return managers.join(', ');
    }

    const fieldLeads = detail.fieldLeads?.filter(x => (x || '').trim().length > 0) ?? [];
    return fieldLeads.length > 0 ? fieldLeads.join(', ') : '-';
  }

  isGiProject(detail: DashboardActiveProjectDetails): boolean {
    const businessUnit = (detail.businessUnitName ?? '').trim().toUpperCase();
    return businessUnit === 'GI' || businessUnit.startsWith('GI ') || businessUnit.includes('(GI)');
  }

  formatSkidConfiguration(value?: string | null): string {
    const normalized = (value ?? '').trim();
    if (!normalized) {
      return '-';
    }

    const parts = normalized.split('-').map(x => x.trim());
    if (parts.length !== 7) {
      return normalized;
    }

    const mapped = parts
      .map((raw, index) => ({ index: index + 1, count: Number(raw) }))
      .filter(x => Number.isFinite(x.count) && x.count > 0)
      .map(x => `N${x.index}:${x.count}`);

    return mapped.length > 0 ? mapped.join('|') : '-';
  }

  getIntegrationRows(): { label: string; count: number }[] {
    const rows = this.integrationSummary?.statusCounts ?? [];
    if (!rows.length) {
      return [{ label: 'Not Started', count: 0 }];
    }
    return rows.map(row => ({ label: row.status, count: row.count }));
  }

  getCommissionRows(): { label: string; value: string }[] {
    const groups = this.commissioningSummary?.groups ?? [];
    if (!groups.length) {
      return [{ label: 'No commissioning templates', value: '0/0' }];
    }
    return groups.map(group => ({
      label: group.groupName,
      value: `${group.verified}/${group.total}`
    }));
  }

  getCommissionProgressPercent(value: string): number {
    const [verifiedRaw, totalRaw] = (value ?? '').split('/');
    const verified = Number((verifiedRaw ?? '').trim());
    const total = Number((totalRaw ?? '').trim());

    if (!Number.isFinite(verified) || !Number.isFinite(total) || total <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, (verified / total) * 100));
  }

  toggleCustomerTemplate(projectTemplateId: number): void {
    this.expandedCustomerTemplateId = this.expandedCustomerTemplateId === projectTemplateId ? null : projectTemplateId;
  }

  getCustomerTemplates(): DashboardCustomerTemplateSummary[] {
    return this.customerSkidSummary?.templates ?? [];
  }

  getCustomerRows(projectTemplateId: number): { label: string; count: number }[] {
    const template = this.getCustomerTemplates().find((x) => x.projectTemplateId === projectTemplateId);
    const rows = template?.statusCounts ?? [];
    if (!rows.length) {
      return [{ label: 'Draft', count: 0 }];
    }
    return rows.map(row => ({ label: row.status, count: row.count }));
  }

  getProjectStatusIndicatorClass(project: DashboardActiveProjectDetails): string {
    const indicator = (project.projectStatusIndicator ?? '').trim().toLowerCase();
    if (indicator === 'green') {
      return 'project-status-indicator--green';
    }
    if (indicator === 'yellow') {
      return 'project-status-indicator--yellow';
    }
    return '';
  }

  getProjectStatusIndicatorLabel(project: DashboardActiveProjectDetails): string {
    const indicator = (project.projectStatusIndicator ?? '').trim().toLowerCase();
    if (indicator === 'green') {
      return 'Daily report submitted in the last 4 days';
    }
    if (indicator === 'yellow') {
      return 'Active resources on project; no daily report in the last 4 days';
    }
    return '';
  }

  getManHoursUserOptions(): Array<{ label: string; value: number }> {
    const users = this.projectManHoursAnalytics?.users ?? [];
    return users.map(user => ({ label: user.userName, value: user.userId }));
  }

  onManHoursUsersChange(): void {
    if (!this.projectId) {
      return;
    }

    this.loadProjectManHoursAnalytics(this.projectId);
  }

  toggleDateRangePopover(): void {
    this.showDateRangePopover = !this.showDateRangePopover;
    this.dateRangeDraft = { ...this.manHoursDateRange };
    this.dateRangeError = '';
  }

  onDateRangeInput(field: 'startDate' | 'endDate', value: string): void {
    this.dateRangeDraft = {
      ...this.dateRangeDraft,
      [field]: value
    };
    this.dateRangeError = '';
  }

  canApplyDateRange(): boolean {
    const { startDate, endDate } = this.dateRangeDraft;
    return !!startDate && !!endDate && startDate <= endDate;
  }

  applyDateRange(): void {
    if (!this.canApplyDateRange()) {
      this.dateRangeError = 'Start date must be before end date.';
      return;
    }

    this.manHoursDateRange = { ...this.dateRangeDraft };
    this.showDateRangePopover = false;
    this.dateRangeError = '';

    if (this.projectId) {
      this.loadProjectManHoursAnalytics(this.projectId);
    }
  }

  closeDateRangePopover(): void {
    this.showDateRangePopover = false;
    this.dateRangeError = '';
  }

  getDateRangeLabel(): string {
    const { startDate, endDate } = this.manHoursDateRange;
    if (!startDate || !endDate) {
      return 'Date Range';
    }

    return `${this.formatDateLabel(startDate)} - ${this.formatDateLabel(endDate)}`;
  }

  downloadCumulativeHoursPerUser(): void {
    if (!this.projectManHoursAnalytics || !this.projectId) {
      return;
    }

    this.dashboardService
      .downloadProjectCumulativeHours(
        this.projectId,
        this.manHoursDateRange.startDate || undefined,
        this.manHoursDateRange.endDate || undefined,
        this.selectedManHoursUserIds
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.buildProjectExportFileName('Cumulative_Hours_Per_User')}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.analyticsErrorMessage = 'Failed to download cumulative hours report.';
        }
      });
  }

  downloadHoursAllocationBySkid(): void {
    if (!this.projectManHoursAnalytics || !this.projectId) {
      return;
    }

    this.dashboardService
      .downloadProjectHoursAllocationBySkid(
        this.projectId,
        this.manHoursDateRange.startDate || undefined,
        this.manHoursDateRange.endDate || undefined,
        this.selectedManHoursUserIds
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.buildProjectExportFileName('Hours_Allocation_By_Equipment')}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.analyticsErrorMessage = 'Failed to download hours allocation by equipment report.';
        }
      });
  }

  getFormsByShortCode(shortCode: 'WMR' | 'NCR'): DashboardProjectFormByShortCode[] {
    return shortCode === 'WMR' ? this.wmrForms : this.ncrForms;
  }

  getSkidBreakdownChartWidthPx(fullscreen = false): number {
    const labels = this.skidBreakdownChartData?.labels;
    const skidCount = Array.isArray(labels) ? labels.length : 0;
    const minWidth = fullscreen ? 960 : 560;
    const perSkidWidth = fullscreen ? 56 : 48;
    const endPadding = fullscreen ? 96 : 48;

    return Math.max(minWidth, skidCount * perSkidWidth + endPadding);
  }

  isFormsLoading(shortCode: 'WMR' | 'NCR'): boolean {
    return shortCode === 'WMR' ? this.wmrLoading : this.ncrLoading;
  }

  formatProjectFormDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  private loadProjectManHoursAnalytics(projectId: number): void {
    this.analyticsLoading = true;
    this.analyticsErrorMessage = '';

    this.dashboardService
      .getProjectManHoursAnalytics(
        projectId,
        this.manHoursDateRange.startDate || undefined,
        this.manHoursDateRange.endDate || undefined,
        this.selectedManHoursUserIds
      )
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.analyticsErrorMessage = 'Failed to load project man hours analytics.';
          return of(null);
        })
      )
      .subscribe((result) => {
        this.projectManHoursAnalytics = result;

        const availableUserIds = new Set((result?.users ?? []).map(x => x.userId));
        this.selectedManHoursUserIds = this.selectedManHoursUserIds.filter(id => availableUserIds.has(id));

        this.refreshProjectManHoursCharts();
        this.analyticsLoading = false;
      });
  }

  private loadProjectFormsByShortCode(projectId: number, shortCode: 'WMR' | 'NCR'): void {
    if (shortCode === 'WMR') {
      this.wmrLoading = true;
      this.wmrForms = [];
    } else {
      this.ncrLoading = true;
      this.ncrForms = [];
    }

    this.dashboardService.getProjectFormsByTemplateShortCode(projectId, shortCode)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([] as DashboardProjectFormByShortCode[]))
      )
      .subscribe((rows) => {
        if (shortCode === 'WMR') {
          this.wmrForms = rows ?? [];
          this.wmrLoading = false;
        } else {
          this.ncrForms = rows ?? [];
          this.ncrLoading = false;
        }
      });
  }

  private buildProjectExportFileName(reportName: string): string {
    const projectName = this.projectDetails?.projectName || `Project_${this.projectId ?? ''}`;
    return `${projectName}_${reportName}`;
  }

  private refreshProjectManHoursCharts(): void {
    const analytics = this.projectManHoursAnalytics;
    if (!analytics) {
      this.manHoursChartData = { labels: [], datasets: [] };
      this.categoryBreakdownChartData = { labels: [], datasets: [] };
      this.skidBreakdownChartData = { labels: [], datasets: [] };
      this.manHoursCategoryColors.clear();
      return;
    }

    const labels = (analytics.labels ?? []).map(label => this.formatDateLabel(label));
    const series = analytics.cumulativeSeries ?? [];
    const maxValue = series.length > 0
      ? Math.max(...series.flatMap(item => item.hours ?? []))
      : 0;
    this.manHoursYAxis = this.buildProjectManHoursYAxis(Number(maxValue) || 0);

    this.manHoursChartData = {
      labels,
      datasets: series.map((item, index) => {
        const color = this.manHoursPalette[index % this.manHoursPalette.length];
        return {
          label: item.userName,
          data: item.hours,
          borderColor: color,
          backgroundColor: this.toTransparentColor(color, 0.12),
          pointBackgroundColor: color,
          pointBorderColor: color,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          tension: 0,
          fill: true
        };
      })
    };
    this.manHoursChartOptions = this.buildProjectManHoursChartOptions();

    const categories = analytics.categoryBreakdown ?? [];
    const skidCategories = analytics.skidCategoryBreakdown ?? [];
    this.assignManHoursCategoryColors([
      ...categories.map(item => item.category),
      ...skidCategories.map(item => item.category)
    ]);

    this.categoryBreakdownChartData = {
      labels: categories.map(item => item.category),
      datasets: [
        {
          data: categories.map(item => item.hours),
          backgroundColor: categories.map(item => this.getManHoursCategoryColor(item.category)),
          borderWidth: 1,
          borderColor: '#ffffff'
        }
      ]
    };

    this.skidBreakdownChartData = this.buildSkidCategoryBarData(skidCategories);
  }

  private buildProjectManHoursChartOptions(): any {
    const yTicks = this.manHoursYAxis;
    const yMax = yTicks.length > 0 ? yTicks[0] : 0;
    const yStep = yTicks.length > 1 ? yTicks[0] - yTicks[1] : 1;

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context: any) => `${context.dataset.label}: ${context.parsed.y}h`
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#787e91',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          border: {
            color: '#e9e9ed'
          }
        },
        y: {
          beginAtZero: true,
          min: 0,
          max: yMax > 0 ? yMax : undefined,
          ticks: {
            stepSize: yStep > 0 ? yStep : 1,
            precision: 0,
            color: '#787e91',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          grid: {
            color: '#e9e9ed'
          },
          border: {
            display: false
          }
        }
      }
    };
  }

  private buildProjectManHoursYAxis(maxValue: number): number[] {
    if (!Number.isFinite(maxValue) || maxValue <= 0) {
      return [0];
    }

    const paddedMax = maxValue * 1.1;
    const step = Math.max(1, Math.ceil(this.roundProjectManHoursStep(paddedMax / 5)));
    const top = Math.max(step * 5, Math.ceil(paddedMax / step) * step);

    return Array.from({ length: 6 }, (_, index) => top - (index * step));
  }

  private roundProjectManHoursStep(rawStep: number): number {
    const safe = Math.max(0.5, rawStep);
    if (safe <= 1) {
      return 1;
    }
    if (safe <= 2) {
      return 2;
    }
    if (safe <= 5) {
      return 5;
    }
    if (safe <= 10) {
      return 10;
    }

    return Math.ceil(safe / 5) * 5;
  }

  private buildCategoryBreakdownChartOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          right: 28
        }
      },
      plugins: {
        legend: {
          position: 'right',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            color: '#606576',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label ?? '';
              const value = Number(context.parsed ?? 0);
              const dataset = context.dataset?.data ?? [];
              const total = dataset.reduce((sum: number, item: number) => sum + Number(item || 0), 0);
              const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
              return `${label}: ${value}h (${percent}%)`;
            }
          }
        }
      }
    };
  }

  private buildSkidBreakdownChartOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            color: '#606576',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => `${context.dataset?.label ?? 'Category'}: ${Number(context.parsed?.y ?? 0)}h`
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#787e91',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          border: {
            color: '#e9e9ed'
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            color: '#787e91',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          grid: {
            color: '#e9e9ed'
          },
          border: {
            display: false
          }
        }
      }
    };
  }

  private buildSkidCategoryBarData(rows: Array<{ skid: string; category: string; hours: number }>): any {
    if (!rows || rows.length === 0) {
      return { labels: [], datasets: [] };
    }

    const skidSet = new Set<string>();
    const categorySet = new Set<string>();
    const cellHours = new Map<string, number>();

    rows.forEach(row => {
      const skid = this.normalizeSkidLabel(row.skid);
      const category = (row.category || '').trim() || 'Uncategorized';
      const hours = Number(row.hours) || 0;
      skidSet.add(skid);
      categorySet.add(category);
      const key = `${skid}__${category}`;
      cellHours.set(key, (cellHours.get(key) ?? 0) + hours);
    });

    const labels = Array.from(skidSet).sort((a, b) => a.localeCompare(b));
    const categories = Array.from(categorySet).sort((a, b) => a.localeCompare(b));

    const datasets = categories.map((category, index) => ({
      label: category,
      data: labels.map(skid => cellHours.get(`${skid}__${category}`) ?? 0),
      backgroundColor: this.getManHoursCategoryColor(category),
      borderWidth: 0,
      borderRadius: 3,
      barThickness: 16,
      maxBarThickness: 20
    }));

    return {
      labels,
      datasets
    };
  }

  private assignManHoursCategoryColors(categories: Array<string | null | undefined>): void {
    this.manHoursCategoryColors.clear();

    const normalizedCategories = Array.from(new Set(
      categories.map(category => this.normalizeManHoursCategory(category))
    )).sort((first, second) => first.localeCompare(second));

    normalizedCategories.forEach((category, index) => {
      this.manHoursCategoryColors.set(category, this.getHighContrastCategoryColor(index));
    });
  }

  private getManHoursCategoryColor(category?: string | null): string {
    const normalizedCategory = this.normalizeManHoursCategory(category);
    const assignedColor = this.manHoursCategoryColors.get(normalizedCategory);
    if (assignedColor) {
      return assignedColor;
    }

    const color = this.getHighContrastCategoryColor(this.manHoursCategoryColors.size);
    this.manHoursCategoryColors.set(normalizedCategory, color);
    return color;
  }

  private normalizeManHoursCategory(category?: string | null): string {
    return (category || '').trim().replace(/\s+/g, ' ').toUpperCase() || 'UNCATEGORIZED';
  }

  private getHighContrastCategoryColor(index: number): string {
    if (index < this.manHoursCategoryPalette.length) {
      return this.manHoursCategoryPalette[index];
    }

    // Golden-angle hue spacing keeps additional, unforeseen categories apart.
    const hue = Math.round((index * 137.508) % 360);
    const lightness = index % 2 === 0 ? 38 : 50;
    return `hsl(${hue}, 68%, ${lightness}%)`;
  }

  private normalizeSkidLabel(value?: string | null): string {
    const normalized = (value || '').trim();
    if (!normalized) {
      return 'Unassigned';
    }

    return normalized === '-1' ? 'NA' : normalized;
  }

  private formatDateLabel(value: string): string {
    const parts = value.split('-');
    if (parts.length !== 3) {
      return value;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]) - 1;
    const day = Number(parts[2]);
    const parsed = new Date(year, month, day);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private getCurrentMonthDateRange(): { startDate: string; endDate: string } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const format = (value: Date): string => {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: format(start),
      endDate: format(end)
    };
  }

  private toTransparentColor(hexColor: string, alpha: number): string {
    const value = hexColor.replace('#', '');
    if (value.length !== 6) {
      return `rgba(61, 91, 131, ${alpha})`;
    }

    const r = parseInt(value.substring(0, 2), 16);
    const g = parseInt(value.substring(2, 4), 16);
    const b = parseInt(value.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private resolveUserId() {
    const current = this.ssoService.getCurrentUser();
    if (current?.userId) {
      return of(current.userId);
    }

    return this.ssoService.user$.pipe(
      filter(user => !!user?.userId),
      take(1),
      map(user => user?.userId ?? 0)
    );
  }
}
